<template>
  <!-- 頁面標題 -->
  <div class="page-header">
    <h1>管理帳號設定</h1>
    <button class="btn-primary-add" @click="showAddModal = true">
      + 新增員工
    </button>
  </div>

  <!-- 搜尋與篩選 -->
  <div class="search-filter">
    <div class="search-box">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜尋員工編號、姓名、信箱或電話..."
        @input="handleSearch"
      />
    </div>

    <div class="filter-dropdown">
      <select v-model="activeFilter" class="form-input">
        <option
          v-for="filter in filters"
          :key="filter.value"
          :value="filter.value"
        >
          {{ filter.label }} ({{ filter.count ?? 0 }})
        </option>
      </select>
    </div>
  </div>

  <!-- 手機版 - 員工卡片列表 -->
  <div class="employee-list-mobile">
    <div
      class="employee-card"
      v-for="employee in paginatedEmployees"
      :key="employee.EmployeeCode"
    >
      <!-- 員工標題列 -->
      <div class="employee-header">
        <div class="employee-title-section">
          <div class="employee-basic-info">
            <h3 class="employee-name">{{ employee.FullName }}</h3>
            <div class="employee-code">{{ employee.EmployeeCode }}</div>
          </div>

          <div class="badges-group">
            <span
              class="role-badge"
              :style="{
                backgroundColor: `${
                  roleTypes[employee.Role]?.color || '#999'
                }20`,
                color: roleTypes[employee.Role]?.color || '#999',
                border: `1px solid ${
                  roleTypes[employee.Role]?.color || '#999'
                }`,
              }"
            >
              {{
                roleTypes[employee.Role]?.label || employee.Role || "未知角色"
              }}
            </span>
            <span class="status-badge" :class="{ active: employee.IsActive }">
              {{ employee.IsActive ? "在職" : "停用" }}
            </span>
          </div>
        </div>
      </div>

      <!-- 員工資訊 -->
      <div class="employee-info">
        <div class="info-row">
          <span class="info-label">帳號</span>
          <span class="info-value">{{ employee.Account }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">信箱</span>
          <span class="info-value">{{ employee.Email }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">電話</span>
          <span class="info-value">{{ employee.PhoneNumber }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">創建日期</span>
          <span class="info-value">{{ formatDate(employee.CreateDate) }}</span>
        </div>
      </div>

      <!-- 員工操作按鈕 -->
      <div class="employee-actions">
        <button
          class="btn-action btn-view"
          @click="viewEmployeeDetail(employee)"
        >
          查看
        </button>
        <button class="btn-action btn-edit" @click="editEmployee(employee)">
          編輯
        </button>
        <button
          class="btn-action btn-toggle"
          @click="toggleEmployeeStatus(employee)"
        >
          {{ employee.IsActive ? "停用" : "啟用" }}
        </button>
        <button class="btn-action btn-reset" @click="resetPassword(employee)">
          重設密碼
        </button>
        <button
          class="btn-action btn-delete"
          @click="deleteEmployee(employee.EmployeeCode)"
        >
          刪除
        </button>
      </div>
    </div>
  </div>

  <!-- 手機版分頁 -->
  <div class="pagination-container mobile-pagination">
    <button
      class="pagination-btn"
      :disabled="currentPage === 1"
      @click="prevPage"
    >
      ⬅ 上一頁
    </button>

    <span class="pagination-info">
      第 {{ currentPage }} / {{ totalPages }} 頁
    </span>

    <button
      class="pagination-btn"
      :disabled="currentPage === totalPages"
      @click="nextPage"
    >
      下一頁 ➡
    </button>
  </div>

  <!-- 電腦版 - 員工表格 -->
  <div class="employee-table-desktop">
    <div class="table-container">
      <table class="employee-table">
        <thead>
          <tr>
            <th>員工編號</th>
            <th>員工姓名</th>
            <th>帳號</th>
            <th>信箱</th>
            <th>電話</th>
            <th>創建日期</th>
            <th style="text-align: center">權限</th>
            <th style="text-align: center">狀態</th>
            <th style="text-align: center">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="employee in paginatedEmployees"
            :key="employee.EmployeeCode"
            class="table-row"
          >
            <td class="code-cell">
              <span class="code-text">{{ employee.EmployeeCode }}</span>
            </td>
            <td>
              <div class="employee-cell">
                <div class="employee-info-text">
                  <div class="employee-name-text">
                    {{ employee.FullName }}
                  </div>
                </div>
              </div>
            </td>
            <td>{{ employee.Account }}</td>
            <td>{{ employee.Email }}</td>
            <td>{{ employee.PhoneNumber }}</td>
            <td>{{ formatDate(employee.CreateDate) }}</td>
            <td class="role-cell">
              <span
                class="role-badge"
                :style="{
                  backgroundColor: `${
                    roleTypes[employee.Role]?.color || '#999'
                  }20`,
                  color: roleTypes[employee.Role]?.color || '#999',
                  border: `1px solid ${
                    roleTypes[employee.Role]?.color || '#999'
                  }`,
                }"
              >
                {{
                  roleTypes[employee.Role]?.label || employee.Role || "未知角色"
                }}
              </span>
            </td>
            <td class="status-cell">
              <span class="status-badge" :class="{ active: employee.IsActive }">
                {{ employee.IsActive ? "在職" : "停用" }}
              </span>
            </td>
            <td class="actions-cell">
              <div class="table-actions">
                <button
                  class="btn-table-action"
                  @click="viewEmployeeDetail(employee)"
                  title="查看詳情"
                >
                  <img src="@/assets/image/icon/search.png" class="all-icon" />
                </button>

                <button
                  class="btn-table-action"
                  @click="editEmployee(employee)"
                  title="編輯"
                >
                  <img src="@/assets/image/icon/edit.png" class="all-icon" />
                </button>

                <button
                  class="btn-table-action"
                  @click="toggleEmployeeStatus(employee)"
                  :title="employee.IsActive ? '停用' : '啟用'"
                >
                  <img
                    :src="
                      employee.IsActive
                        ? require('@/assets/image/icon/unlock.png')
                        : require('@/assets/image/icon/redlock.png')
                    "
                    class="all-icon"
                  />
                </button>

                <button
                  class="btn-table-action"
                  @click="resetPassword(employee)"
                  title="重設密碼"
                >
                  <img
                    src="@/assets/image/icon/user-key.png"
                    class="all-icon"
                  />
                </button>
                <button
                  class="btn-table-action delete"
                  @click="deleteEmployee(employee.EmployeeCode)"
                  title="刪除"
                >
                  <img src="@/assets/image/icon/trash.png" class="all-icon" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 電腦版分頁 -->
      <div class="pagination-container">
        <button
          class="pagination-btn"
          :disabled="currentPage === 1"
          @click="prevPage"
        >
          ⬅ 上一頁
        </button>

        <span class="pagination-info">
          第 {{ currentPage }} / {{ totalPages }} 頁
        </span>

        <button
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="nextPage"
        >
          下一頁 ➡
        </button>
      </div>
    </div>
  </div>

  <!-- 查看員工詳情 Modal -->
  <div class="modal" v-if="showDetailModal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>員工詳情 - {{ selectedEmployee?.FullName }}</h2>
        <button class="close-btn" @click="showDetailModal = false">×</button>
      </div>

      <div class="modal-body">
        <div class="detail-section">
          <h3 class="section-title">基本資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">員工編號</span>
              <span class="detail-value">{{
                selectedEmployee?.EmployeeCode
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">員工姓名</span>
              <span class="detail-value">{{ selectedEmployee?.FullName }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">帳號</span>
              <span class="detail-value">{{ selectedEmployee?.Account }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">權限</span>
              <span class="detail-value">
                <span
                  class="role-badge"
                  :style="{
                    backgroundColor: `${
                      roleTypes[selectedEmployee?.Role]?.color || '#999'
                    }20`,
                    color: roleTypes[selectedEmployee?.Role]?.color || '#999',
                    border: `1px solid ${
                      roleTypes[selectedEmployee?.Role]?.color || '#999'
                    }`,
                  }"
                >
                  {{
                    roleTypes[selectedEmployee?.Role]?.label ||
                    selectedEmployee?.Role ||
                    "未知角色"
                  }}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">聯絡資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">電子信箱</span>
              <span class="detail-value">{{ selectedEmployee?.Email }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">聯絡電話</span>
              <span class="detail-value">{{
                selectedEmployee?.PhoneNumber
              }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">日期資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">創建日期</span>
              <span class="detail-value">{{
                formatDate(selectedEmployee?.CreateDate)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">在職狀態</span>
              <span class="detail-value">
                <span
                  class="status-badge"
                  :class="{ active: selectedEmployee?.IsActive }"
                >
                  {{ selectedEmployee?.IsActive ? "在職" : "停用" }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">資料建立時間</span>
              <span class="detail-value">{{
                formatDate(selectedEmployee?.CreateDate)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">最後更新時間</span>
              <span class="detail-value">{{
                formatDate(selectedEmployee?.UpdateDate)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="showDetailModal = false">
          關閉
        </button>
        <button class="btn-submit" @click="editEmployee(selectedEmployee)">
          編輯員工
        </button>
      </div>
    </div>
  </div>

  <!-- 新增/編輯員工 Modal -->
  <div class="modal" v-if="showAddModal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ editingEmployee ? "編輯員工" : "新增員工" }}</h2>
        <button class="close-btn" @click="closeModal">×</button>
      </div>

      <form @submit.prevent="saveEmployee" class="modal-body">
        <div class="form-row">
          <div class="form-group-number">
            <label class="form-label required">員工編號</label>
            <input
              type="text"
              class="form-input"
              v-model="formData.EmployeeCode"
              placeholder="請輸入員工編號"
              :disabled="editingEmployee"
            />
          </div>

          <div class="form-group-drop">
            <label class="form-label required">權限</label>
            <select class="form-input" v-model="formData.Role" required>
              <option value="">請選擇權限</option>
              <option
                v-for="(roleInfo, roleName) in editableRoles"
                :key="roleName"
                :value="roleName"
              >
                {{ roleInfo.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label required">員工姓名</label>
          <input
            type="text"
            class="form-input"
            v-model="formData.FullName"
            placeholder="請輸入員工姓名"
            required
          />
        </div>

        <div class="form-group">
          <label class="form-label required">帳號</label>
          <input
            type="text"
            class="form-input"
            v-model="formData.Account"
            placeholder="請輸入登入帳號"
            required
            :disabled="editingEmployee"
          />
        </div>

        <div class="form-group">
          <label class="form-label required">電子信箱</label>
          <input
            type="email"
            class="form-input"
            v-model="formData.Email"
            placeholder="請輸入電子信箱"
            required
            :disabled="editingEmployee"
          />
        </div>

        <div class="form-group">
          <label class="form-label">聯絡電話</label>
          <input
            type="tel"
            class="form-input"
            v-model="formData.PhoneNumber"
            placeholder="請輸入聯絡電話"
          />
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="closeModal">
            取消
          </button>
          <button type="submit" class="btn-submit">
            {{ editingEmployee ? "儲存變更" : "新增員工" }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- 重設密碼 Modal -->
  <div class="modal" v-if="showResetModal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>重設密碼</h2>
        <button class="close-btn" @click="showResetModal = false">×</button>
      </div>

      <div class="modal-body">
        <p class="reset-info">
          確定要重設
          <strong>{{ selectedEmployee?.FullName }}</strong> 的密碼嗎?
        </p>
        <p class="reset-notice">
          系統將自動產生重設信件,並寄送至信箱:<br />
          <strong>{{ selectedEmployee?.Email }}</strong>
        </p>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="btn-cancel"
          @click="showResetModal = false"
        >
          取消
        </button>
        <button
          type="button"
          class="btn-submit"
          :disabled="resetCountdownMap[selectedEmployee?.Id] > 0"
          :class="{ disabledBtn: resetCountdownMap[selectedEmployee?.Id] > 0 }"
          @click="confirmResetPassword"
        >
          {{
            resetCountdownMap[selectedEmployee?.Id] > 0
              ? `請稍候(${resetCountdownMap[selectedEmployee?.Id]}s)`
              : "確認重設"
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<script src="@/assets/js/account.js"></script>
<style scoped src="@/assets/css/account.css"></style>
